import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'
import { useNow } from '../hooks/useNow'

type TimelineItem = {
  type: 'skill' | 'job'
  label: string
  sub?: string
  end_date: string
}

const MAX_ITEMS = 6

/**
 * Timeline combinado: próximos skills en cola + próximos industry jobs.
 * Reusa los queries existentes (TanStack los cachea), así que abrir el
 * dashboard no paga fetch extra.
 *
 * Ordenado por end_date asc. Muestra hasta MAX_ITEMS.
 */
export function UpcomingTimeline() {
  const { data: skills } = useQuery({
    queryKey: ['skill-queue'],
    queryFn: () => api.skillQueue(),
  })
  const { data: jobs } = useQuery({
    queryKey: ['industry-jobs', false],
    queryFn: () => api.industryJobs(false),
  })

  // 1s interval para que los countdowns relativos se actualicen vivos.
  const now = useNow(1000)

  const items = useMemo<TimelineItem[]>(() => {
    const out: TimelineItem[] = []

    for (const s of skills?.queue ?? []) {
        if (!s.finish_date) continue
        out.push({
          type: 'skill',
          label: s.name,
          sub: `→ L${s.finished_level}`,
          end_date: s.finish_date,
        })
    }

    for (const j of jobs?.jobs ?? []) {
      // Solo jobs que todavía no terminaron
      if (j.status === 'delivered' || j.status === 'cancelled' || j.status === 'reverted') {
        continue
      }
      out.push({
        type: 'job',
        label: j.blueprint_name,
        sub: j.activity_name + (j.runs > 1 ? ` × ${j.runs}` : ''),
        end_date: j.end_date,
      })
    }

    out.sort((a, b) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime())
    return out.slice(0, MAX_ITEMS)
  }, [skills, jobs])

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Próximos eventos</h2>
        <span className="text-xs text-eve-muted">
          {items.length > 0
            ? `Mostrando ${items.length}`
            : 'Sin eventos próximos'}
        </span>
      </div>

      {!items.length ? (
        <div className="text-eve-muted text-sm py-4 text-center">
          No tenés skills en cola ni jobs activos. El timeline se llena
          automáticamente cuando empiezas algo.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((item, i) => (
            <TimelineRow
              key={`${item.type}-${i}-${item.end_date}`}
              item={item}
              now={now}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function TimelineRow({ item, now }: { item: TimelineItem; now: number }) {
  const endMs = new Date(item.end_date).getTime()
  const remainingSec = Math.max(0, Math.round((endMs - now) / 1000))

  const accent =
    item.type === 'skill'
      ? 'border-l-eve-accent'
      : 'border-l-yellow-500'

  return (
    <li
      className={`flex items-center justify-between border-l-2 ${accent} pl-3 pr-2 py-1.5`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{item.label}</div>
        {item.sub && (
          <div className="text-xs text-eve-muted">{item.sub}</div>
        )}
      </div>
      <div className="text-right shrink-0 ml-3">
        <div className="text-sm font-mono">{formatRemaining(remainingSec)}</div>
        <div className="text-[10px] text-eve-muted uppercase tracking-wide">
          {item.type === 'skill' ? 'skill' : 'job'}
        </div>
      </div>
    </li>
  )
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'listo'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}